
import React from 'react';

import { Container } from '../../react/base';
import { styleTypeToObject } from '../../styles';

type Props = {

    /**
     * Class name for the web platform, if any.
     */
    className?: string,

    /**
     * Color of the icon (if not provided by the style object).
     * Applied as CSS {@code color} so stroke-based SVGs using
     * {@code currentColor} pick it up, and as the SVG {@code fill}
     * for fill-based icons.
     */
    color?: string,

    /**
     * Id prop (mainly for autotests).
     */
    id?: string,

    /**
     * Function to invoke on click.
     */
    onClick?: Function,

    /**
     * The size of the icon (if not provided by the style object).
     */
    size?: number | string,

    /**
     * The preloaded icon component to render.
     */
    src: Function,

    /**
     * Optional stroke override. When omitted, stroke-based icons using
     * {@code currentColor} follow {@link color} / inherited CSS color.
     */
    strokeColor?: string,

    /**
     * Style object to be applied.
     */
    style?: Object,

    fillColor?:string
};

export const DEFAULT_COLOR = navigator.product === 'ReactNative' ? 'white' : undefined;
export const DEFAULT_SIZE = navigator.product === 'ReactNative' ? 36 : 26;

/**
 * Implements an Icon component that takes a loaded SVG file as prop and renders it as an icon.
 *
 * @param {Props} props - The props of the component.
 * @returns {Reactelement}
 */
export default function Icon(props: Props) {
    const {
        className = '',
        color,
        id,
        onClick,
        size,
        strokeColor,
        src: IconComponent,
        style,
        fillColor
    } = props;


    const {
        color: styleColor,
        fontSize: styleSize,
        ...restStyle
    } = styleTypeToObject(style ?? {});

    // Only an explicit color should recolor stroke="currentColor" icons.
    // Leaving color unset preserves each call site's inherited CSS color
    // (password fields, etc.) instead of forcing a global default.
    const explicitColor = color ?? styleColor;
    const calculatedColor = explicitColor ?? DEFAULT_COLOR;
    const calculatedSize = size ?? styleSize ?? DEFAULT_SIZE;

    return (
        <Container
            className = { `meethour-icon ${className}` }
            onClick = { onClick }
            style = { explicitColor
                ? { ...restStyle,
                    color: explicitColor }
                : restStyle }>
            <IconComponent
                fill = { calculatedColor }
                height = { calculatedSize }
                id = { id }
                style = {{
                    color: explicitColor,
                    fill: fillColor,
                    stroke: strokeColor
                }}
                width = { calculatedSize } />

        </Container>
    );
}
